import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const ColorObjects: Record<string, string[]> = {
    Red: ["🍎", "🚗", "🎈", "🥊", "🦞", "🌹", "🍓", "📕"],
    Blue: ["💙", "🚙", "🌊", "🧢", "🐟", "🦋", "🔷", "📘"],
    Green: ["🍏", "🌳", "🐸", "🥒", "🍀", "🦜", "🧩", "📗"],
    Yellow: ["🌟", "🍌", "🚖", "🌻", "🐤", "🧀", "⚠️", "📒"],
    Orange: ["🦊", "🏀", "🍊", "📙", "🌅", "🦁", "🥕", "🔶"]
};

const PickColorChallenge: React.FC = () => {
    const [randomColor, setRandomColor] = useState<string>("");
    const [allObjects, setAllObjects] = useState<{ color: string; emoji: string }[]>([]);
    const [selectedObjects, setSelectedObjects] = useState<string[]>([]);
    const [message, setMessage] = useState<string>("");
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewChallenge();
        document.title = "Pick the Color – Nursery Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const shuffleArray = (array: any[]) => array.sort(() => Math.random() - 0.5);

    const generateNewChallenge = () => {
        const colors = Object.keys(ColorObjects);
        const newRandomColor = colors[Math.floor(Math.random() * colors.length)];
        setRandomColor(newRandomColor);

        const mixedObjects: { color: string; emoji: string }[] = [];

        // Pick 3 objects from the selected color
        const correctObjects = shuffleArray([...ColorObjects[newRandomColor]]).slice(0, 3);
        correctObjects.forEach(emoji => mixedObjects.push({ color: newRandomColor, emoji }));

        // Pick 3 other random colors (excluding the selected color)
        const otherColors = shuffleArray(colors.filter(c => c !== newRandomColor)).slice(0, 3);

        // Pick 3 objects from each of the 3 wrong colors (total 9 incorrect objects)
        otherColors.forEach(color => {
            const wrongObjects = shuffleArray([...ColorObjects[color]]).slice(0, 3);
            wrongObjects.forEach(emoji => mixedObjects.push({ color, emoji }));
        });

        setAllObjects(shuffleArray(mixedObjects));
        setSelectedObjects([]);
        setMessage("");
    };

    const handleSelect = (emoji: string) => {
        setSelectedObjects(prev =>
            prev.includes(emoji) ? prev.filter(e => e !== emoji) : [...prev, emoji]
        );
    };

    const checkAnswer = () => {
        const correctObjects = ColorObjects[randomColor];
        const isCorrect = selectedObjects.length === 3 &&
            selectedObjects.every(obj => correctObjects.includes(obj));

        setMessage(isCorrect ? "✅ Correct! Well done!" : "❌ Try Again!");
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Make color learning fun with the "Pick the Color" activity on Mamas Guidance—an interactive exercise designed to help nursery kids identify and select colors, boosting visual recognition and early learning skills.' />
            </Helmet>
            <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center" }}>
                <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                <IonModal
                    ref={modal}
                    trigger="open-modal"
                    initialBreakpoint={1}
                    breakpoints={[0, 0.5, 0.75, 1]}
                    handleBehavior="cycle"
                    backdropDismiss={false}  // ✅ Prevents click-to-close
                    className="full-page-modal"
                >
                    <IonContent className="ion-padding">
                        <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                        <h1>Pick the Color</h1>

                        <p>
                            Introduce your little learner to the wonderful world of colors with our interactive <strong>Pick the Color</strong> worksheet! This fun-filled activity helps nursery children <strong>identify and choose the correct color</strong> using engaging images and options.
                        </p>

                        <h2>🎨 What’s in the Worksheet?</h2>
                        <p>
                            Kids will be shown colorful pictures of objects like an <b><em>apple, banana, sun, sky, grass</em></b>, etc. Each question will ask them to choose the correct color from 2–3 given options. It’s a perfect mix of visual learning and playful interaction.
                        </p>

                        <h2>🎯 What Your Child Will Learn</h2>
                        <ul>
                            <li>Identify basic colors: <strong>red, blue, green, yellow, orange</strong>, etc.</li>
                            <li>Associate colors with familiar real-world objects</li>
                            <li>Develop visual discrimination and observation skills</li>
                            <li>Build vocabulary and early decision-making ability</li>
                        </ul>

                        <h2>👩‍🏫 How to Use the Worksheet</h2>
                        <ul>
                            <li>Show your child each picture and ask them to name it.</li>
                            <li>Ask, <strong>“What color is this?”</strong> and let them choose the correct one.</li>
                            <li>Encourage them to circle or color the correct option.</li>
                            <li>Reinforce the learning with real objects of the same color.</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Guess the Area</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/general-awareness/colors/match-color"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Match Same Color</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Guess Community Helper</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🧠 Why This Activity Is Valuable</h2>
                        <p>
                            Color recognition is a key part of early development. It boosts <strong>memory, concentration, and environmental awareness</strong> — while also being fun! This worksheet gives children a head start in recognizing colors around them.
                        </p>

                        <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                    </IonContent>
                </IonModal>
                <article className="visually-hidden">
                    <h1>Pick the Color</h1>

                    <p>
                        Introduce your little learner to the wonderful world of colors with our interactive <strong>Pick the Color</strong> worksheet! This fun-filled activity helps nursery children <strong>identify and choose the correct color</strong> using engaging images and options.
                    </p>

                    <h2>🎨 What’s in the Worksheet?</h2>
                    <p>
                        Kids will be shown colorful pictures of objects like an <b><em>apple, banana, sun, sky, grass</em></b>, etc. Each question will ask them to choose the correct color from 2–3 given options. It’s a perfect mix of visual learning and playful interaction.
                    </p>

                    <h2>🎯 What Your Child Will Learn</h2>
                    <ul>
                        <li>Identify basic colors: <strong>red, blue, green, yellow, orange</strong>, etc.</li>
                        <li>Associate colors with familiar real-world objects</li>
                        <li>Develop visual discrimination and observation skills</li>
                        <li>Build vocabulary and early decision-making ability</li>
                    </ul>

                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                    <ul>
                        <li>Show your child each picture and ask them to name it.</li>
                        <li>Ask, <strong>“What color is this?”</strong> and let them choose the correct one.</li>
                        <li>Encourage them to circle or color the correct option.</li>
                        <li>Reinforce the learning with real objects of the same color.</li>
                    </ul>


                    <h2>🔗 Related Topics</h2>
                    <div>
                        <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Guess the Area</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/nursery/general-awareness/colors/match-color"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Match Same Color</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Guess Community Helper</IonLabel>
                        </IonItem>
                    </div>

                    <h2>🧠 Why This Activity Is Valuable</h2>
                    <p>
                        Color recognition is a key part of early development. It boosts <strong>memory, concentration, and environmental awareness</strong> — while also being fun! This worksheet gives children a head start in recognizing colors around them.
                    </p>
                </article>
                <h2>Pick all the {randomColor} objects</h2>

                <div className="fontObject" style={{ margin: "20px 0", flexWrap: "wrap", justifyContent: "center" }}>
                    {allObjects.map((item, index) => (
                        <span
                            key={index}
                            onClick={() => handleSelect(item.emoji)}
                            style={{
                                margin: "10px",
                                cursor: "pointer",
                                padding: "10px",
                                borderRadius: "10px",
                                border: selectedObjects.includes(item.emoji) ? "2px solid black" : "2px solid transparent",
                                userSelect: "none"
                            }}
                        >
                            {item.emoji}
                        </span>
                    ))}
                </div>

                <div className="text-center" style={{ marginBottom: "20px", display: "ruby" }}>
                    <div className="background-blue" onClick={checkAnswer} style={{ fontSize: "16px" }}>Check</div>
                    <div className="btn hero-btn" onClick={generateNewChallenge} style={{ marginLeft: "10px" }}>Next &gt;&gt;</div>
                </div>

                {message && <h3 style={{ marginTop: "20px" }}>{message}</h3>}
            </div>
        </>
    );
};

export default PickColorChallenge;
